// challenge-diff — the pure halves of the challenge "better failures" readout
// (friendlier-challenges, Trello d7cn4sN9). No DOM, no imports beyond the grading
// module's pure kind predicate, so every rule is host-testable
// (test/editor/challenge-diff.test.js).
//
//   lineDiff(expected, got)        aligned line pairs for the Expected / Your program
//                                  printed columns, each `same | changed | missing |
//                                  extra`, with the differing span of a changed line.
//   diffWindow(rows, max, lead)    which rows to show when the output is long.
//   nearMiss(expectedLine, gotLine) the "Almost: …" label for the FIRST differing pair.
//   lookedFor(checkpoint)          one plain sentence saying what a check looked for.
//
// ⚠️ DISPLAY ONLY. Nothing here feeds `passed` or the POSTed `diff`; grading stays the
// exact compare in challenge-grading.js. `expected` / `got` are the NORMALISED strings that
// compare used, so a highlight always matches the verdict.

import { isTemporalKind } from './challenge-grading.js';

function toLines(text) {
  var s = text == null ? '' : String(text);
  return s === '' ? [] : s.split('\n');
}

// The differing span inside a changed pair, by common prefix/suffix trimming. Spans are
// [start, end) offsets into each line; an empty span (start === end) marks an insertion
// point on that side.
function changedSpans(a, b) {
  var pre = 0;
  var max = Math.min(a.length, b.length);
  while (pre < max && a.charAt(pre) === b.charAt(pre)) { pre++; }
  var suf = 0;
  while (suf < max - pre && a.charAt(a.length - 1 - suf) === b.charAt(b.length - 1 - suf)) { suf++; }
  return { expSpan: [pre, a.length - suf], gotSpan: [pre, b.length - suf] };
}

// The longest-common-subsequence size budget. Above it the alignment falls back to plain
// position-by-position pairing (what the grader itself compares) — still correct, just
// less clever about one inserted line.
var LCS_MAX_CELLS = 250000;

// Pairs of matched indices [[i, j], …] for the LCS of two line arrays.
function lcsPairs(a, b) {
  var n = a.length, m = b.length;
  var table = [];
  for (var i = 0; i <= n; i++) { table.push(new Array(m + 1).fill(0)); }
  for (i = n - 1; i >= 0; i--) {
    for (var j = m - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  var pairs = [];
  i = 0; j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { pairs.push([i, j]); i++; j++; }
    else if (table[i + 1][j] >= table[i][j + 1]) { i++; }
    else { j++; }
  }
  return pairs;
}

function pushGap(rows, a, b, i0, i1, j0, j1) {
  var k = 0;
  while (i0 + k < i1 && j0 + k < j1) {
    var e = a[i0 + k], g = b[j0 + k];
    var spans = changedSpans(e, g);
    rows.push({ status: 'changed', expected: e, got: g, expSpan: spans.expSpan, gotSpan: spans.gotSpan });
    k++;
  }
  for (var i = i0 + k; i < i1; i++) { rows.push({ status: 'missing', expected: a[i], got: null }); }
  for (var j = j0 + k; j < j1; j++) { rows.push({ status: 'extra', expected: null, got: b[j] }); }
}

// lineDiff(expected, got) → { rows, firstDiff }. `rows` is one entry per displayed line:
//   { status: 'same',    expected, got }
//   { status: 'changed', expected, got, expSpan: [s, e], gotSpan: [s, e] }
//   { status: 'missing', expected, got: null }   — the program did not print this line
//   { status: 'extra',   expected: null, got }   — the program printed a line too many
// `firstDiff` is the index of the first non-`same` row, or -1.
export function lineDiff(expected, got) {
  var a = toLines(expected), b = toLines(got);
  var rows = [];
  var pairs;
  if ((a.length + 1) * (b.length + 1) <= LCS_MAX_CELLS) {
    pairs = lcsPairs(a, b);
  } else {
    pairs = [];
    for (var p = 0; p < Math.min(a.length, b.length); p++) { if (a[p] === b[p]) { pairs.push([p, p]); } }
  }
  var i = 0, j = 0;
  pairs.forEach(function (pair) {
    pushGap(rows, a, b, i, pair[0], j, pair[1]);
    rows.push({ status: 'same', expected: a[pair[0]], got: b[pair[1]] });
    i = pair[0] + 1; j = pair[1] + 1;
  });
  pushGap(rows, a, b, i, a.length, j, b.length);
  var firstDiff = -1;
  for (var r = 0; r < rows.length; r++) { if (rows[r].status !== 'same') { firstDiff = r; break; } }
  return { rows: rows, firstDiff: firstDiff };
}

export var DIFF_MAX_ROWS = 40;
export var DIFF_LEAD_ROWS = 5;

// Which slice of `rows` to show: everything when it fits, otherwise `max` rows starting
// `lead` rows before the first difference. `before` / `after` count the hidden rows on
// each side (the "… N more lines" notes).
export function diffWindow(rows, firstDiff, max, lead) {
  max = max || DIFF_MAX_ROWS;
  lead = lead == null ? DIFF_LEAD_ROWS : lead;
  var total = (rows || []).length;
  if (total <= max) { return { start: 0, end: total, before: 0, after: 0 }; }
  var start = Math.max(0, (firstDiff > 0 ? firstDiff : 0) - lead);
  if (start + max > total) { start = total - max; }
  var end = start + max;
  return { start: start, end: end, before: start, after: total - end };
}

function lowerCase(s) { return s.toLowerCase(); }
function squashSpaces(s) { return s.replace(/[ \t]+/g, ' ').trim(); }
function dropEndPunctuation(s) { return s.replace(/[.,!?;:]+$/, ''); }
function allThree(s) { return dropEndPunctuation(squashSpaces(lowerCase(s))).trim(); }

// nearMiss(expectedLine, gotLine) → the label string, or null. First match wins; a
// missing/extra line (either side null) gets no label, and neither do equal lines.
export function nearMiss(expectedLine, gotLine) {
  if (typeof expectedLine !== 'string' || typeof gotLine !== 'string') { return null; }
  if (expectedLine === gotLine) { return null; }
  if (lowerCase(expectedLine) === lowerCase(gotLine)) {
    return 'Almost: only capital letters differ.';
  }
  if (squashSpaces(expectedLine) === squashSpaces(gotLine)) {
    return 'Almost: only the spacing differs.';
  }
  if (dropEndPunctuation(expectedLine) === dropEndPunctuation(gotLine)) {
    return 'Almost: only the punctuation at the end differs.';
  }
  if (allThree(expectedLine) === allThree(gotLine)) {
    return 'Almost: only capitals, spacing or end punctuation differ.';
  }
  return null;
}

// The near-miss label of a whole lineDiff: computed on the FIRST differing pair only, and
// only when that pair is a `changed` one.
export function firstNearMiss(diff) {
  if (!diff || diff.firstDiff < 0) { return null; }
  var row = diff.rows[diff.firstDiff];
  return row.status === 'changed' ? nearMiss(row.expected, row.got) : null;
}

var STRUCTURAL_PHRASES = {
  'has-loop': 'a loop',
  'print-in-loop': 'print() inside a loop',
  'has-conditional': 'an if statement'
};

function nameOf(v) { return typeof v === 'string' && v.trim() ? v.trim() : ''; }
function code(v) { return '`' + v + '`'; }

// lookedFor(checkpoint) → one plain sentence (inline markdown: names in backticks) saying
// what the check looked for. ⚠️ It NEVER names an expected value, an expected output line or
// a test input — only the kind of check and the variable / function it is about.
export function lookedFor(cp) {
  cp = cp || {};
  var kind = cp.kind;
  var variable = nameOf(cp.variable);
  if (!kind || kind === 'exact' || kind === 'tolerance') {
    return variable
      ? 'It looked for a variable called ' + code(variable) + ' with the right value.'
      : 'It looked for a variable with the right value.';
  }
  switch (kind) {
    case 'expected-output':
      return 'It ran your program and compared what it printed with the expected output.';
    case 'stdin-transcript':
      return 'It typed in test inputs and compared what your program printed.';
    case 'function-io': {
      var fn = nameOf(cp.functionName);
      var subject = fn ? code(fn + '()') : 'your function';
      return cp.compareMode === 'prints'
        ? 'It called ' + subject + ' with test values and checked what it printed.'
        : 'It called ' + subject + ' with test values and checked what it gave back.';
    }
    case 'runs-clean':
      return 'It ran your program and checked it finishes without an error.';
    case 'raises-on':
      return 'It gave your program a bad input and checked it stops with an error.';
    case 'handles-gracefully':
      return 'It gave your program a bad input and checked it asks again instead of crashing.';
    case 'behavioral':
      if (cp.behavioralType === 'iteration-count') { return 'It counted how many times your loop runs.'; }
      if (cp.behavioralType === 'value-sequence') {
        return variable
          ? 'It watched how ' + code(variable) + ' changes as your program runs.'
          : 'It watched how a variable changes as your program runs.';
      }
      if (cp.behavioralType === 'print-sequence') { return 'It checked the order of what your program prints.'; }
      break;
    case 'structural':
      return 'It checked your code uses ' + (STRUCTURAL_PHRASES[cp.pattern] || 'the right structure') + '.';
    default:
      break;
  }
  if (isTemporalKind(kind)) {
    var watched = variable || nameOf(cp.path) || nameOf(cp.observe);
    if (watched) { return 'It ran your game and watched ' + code(watched) + '.'; }
  }
  var what = nameOf(cp.label) || variable;
  return what ? 'It checked: ' + what + '.' : 'It checked one part of your program.';
}
